import React, { useEffect, useState, useCallback } from 'react';

// Dollar sign rain animation component
export function MoneyRain({ duration = 3000, amount = 30, onComplete }) {
  const [particles, setParticles] = useState([]);
  const [isActive, setIsActive] = useState(true);

  // Generate random particles
  const generateParticles = useCallback(() => {
    const newParticles = [];
    for (let i = 0; i < amount; i++) {
      newParticles.push({
        id: i,
        left: Math.random() * 100, // random horizontal position (%)
        delay: Math.random() * 1.5, // random animation delay
        size: 20 + Math.random() * 30, // random size 20-50px
        rotation: Math.random() * 360, // random initial rotation
        emoji: Math.random() > 0.3 ? '💵' : Math.random() > 0.5 ? '💰' : '💸', // mix of money emojis
        speed: 2 + Math.random() * 2, // random fall speed (seconds)
      });
    }
    return newParticles;
  }, [amount]);

  useEffect(() => {
    setParticles(generateParticles());
    
    // Auto-cleanup after duration
    const timer = setTimeout(() => {
      setIsActive(false);
      if (onComplete) onComplete();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, generateParticles, onComplete]);

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute animate-money-fall"
          style={{
            left: `${particle.left}%`,
            fontSize: `${particle.size}px`,
            animationDelay: `${particle.delay}s`,
            animationDuration: `${particle.speed}s`,
            transform: `rotate(${particle.rotation}deg)`,
          }}
        >
          {particle.emoji}
        </div>
      ))}
      
      <style>{`
        @keyframes money-fall {
          0% {
            transform: translateY(-100px) rotate(0deg);
            opacity: 1;
          }
          25% {
            transform: translateY(25vh) rotate(90deg);
          }
          50% {
            transform: translateY(50vh) rotate(180deg);
          }
          75% {
            transform: translateY(75vh) rotate(270deg);
            opacity: 0.8;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
        .animate-money-fall {
          animation: money-fall ease-out forwards;
        }
      `}</style>
    </div>
  );
}

// Hook to trigger money rain from anywhere
let rainCallback = null;

export function useMoneyRain() {
  const triggerRain = useCallback((options = {}) => {
    if (rainCallback) {
      rainCallback(options);
    }
  }, []);

  return { triggerRain };
}

// Provider component to enable money rain globally
export function MoneyRainProvider({ children }) {
  const [rainConfig, setRainConfig] = useState(null);

  useEffect(() => {
    rainCallback = (options) => {
      setRainConfig({
        duration: options.duration || 3000,
        amount: options.amount || 30,
        key: Date.now(), // Force new instance
      });
    };
    return () => { rainCallback = null; };
  }, []);

  return (
    <>
      {children}
      {rainConfig && (
        <MoneyRain
          key={rainConfig.key}
          duration={rainConfig.duration}
          amount={rainConfig.amount}
          onComplete={() => setRainConfig(null)}
        />
      )}
    </>
  );
}

export default MoneyRain;
